/**
 * Axial hex coordinates with pointy-top orientation.
 *
 * Pointy-top axial neighbour order (clockwise from east):
 *   E (+1,  0), NE (+1, -1), NW ( 0, -1),
 *   W (-1,  0), SW (-1, +1), SE ( 0, +1)
 */

export interface Hex {
  readonly q: number;
  readonly r: number;
}

export interface PixelPoint {
  readonly x: number;
  readonly y: number;
}

export interface HexLayout {
  /** Hex radius in pixels (centre to corner). */
  readonly size: number;
  readonly origin: PixelPoint;
}

const SQRT3 = Math.sqrt(3);

const NEIGHBOUR_OFFSETS: readonly Hex[] = [
  { q: 1, r: 0 },
  { q: 1, r: -1 },
  { q: 0, r: -1 },
  { q: -1, r: 0 },
  { q: -1, r: 1 },
  { q: 0, r: 1 },
];

export function hex(q: number, r: number): Hex {
  return { q, r };
}

export function hexEquals(a: Hex, b: Hex): boolean {
  return a.q === b.q && a.r === b.r;
}

export function hexKey(h: Hex): string {
  return `${h.q},${h.r}`;
}

/** Inverse of `hexKey`: parse a `"q,r"` key back to a `Hex`, or `null` if malformed. */
export function parseHexKey(key: string): Hex | null {
  const [qStr, rStr] = key.split(',');
  const q = Number(qStr);
  const r = Number(rStr);
  if (Number.isNaN(q) || Number.isNaN(r)) return null;
  return { q, r };
}

export function neighbours(h: Hex): Hex[] {
  return NEIGHBOUR_OFFSETS.map((o) => ({ q: h.q + o.q, r: h.r + o.r }));
}

export function distance(a: Hex, b: Hex): number {
  const dq = a.q - b.q;
  const dr = a.r - b.r;
  return (Math.abs(dq) + Math.abs(dr) + Math.abs(dq + dr)) / 2;
}

export function axialToPixel(h: Hex, layout: HexLayout): PixelPoint {
  const x = layout.size * (SQRT3 * h.q + (SQRT3 / 2) * h.r) + layout.origin.x;
  const y = layout.size * ((3 / 2) * h.r) + layout.origin.y;
  return { x, y };
}

export function pixelToAxial(p: PixelPoint, layout: HexLayout): Hex {
  const px = p.x - layout.origin.x;
  const py = p.y - layout.origin.y;
  const qf = ((SQRT3 / 3) * px - (1 / 3) * py) / layout.size;
  const rf = ((2 / 3) * py) / layout.size;
  return hexRound(qf, rf);
}

function hexRound(qf: number, rf: number): Hex {
  const sf = -qf - rf;
  let q = Math.round(qf);
  let r = Math.round(rf);
  const s = Math.round(sf);
  const dq = Math.abs(q - qf);
  const dr = Math.abs(r - rf);
  const ds = Math.abs(s - sf);
  if (dq > dr && dq > ds) {
    q = -r - s;
  } else if (dr > ds) {
    r = -q - s;
  }
  return { q: q + 0, r: r + 0 };
}
